import React, { useState, useEffect } from 'react';
import { X, Package, MapPin, Clock, Truck, CheckCircle, DollarSign, Loader2, AlertCircle } from 'lucide-react';
import { Order, OrderStatus } from '../../types';
import { updateOrderStatus, updatePaymentStatus } from '../../services/orderService';
import { sendOrderStatusUpdate } from '../../services/emailService';

interface OrderDetailModalProps {
  order: Order;
  onClose: () => void;
  onUpdate: () => void;
}

const OrderDetailModal: React.FC<OrderDetailModalProps> = ({ order, onClose, onUpdate }) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [trackingNumber, setTrackingNumber] = useState(order.trackingNumber || '');
  const [showTrackingInput, setShowTrackingInput] = useState(false);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isUpdating) onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, isUpdating]);

  const statusSteps: { status: OrderStatus; label: string; icon: React.ReactNode }[] = [
    { status: 'pending', label: 'Pending', icon: <Clock className="w-4 h-4" strokeWidth={1.5} /> },
    { status: 'payment_received', label: 'Payment received', icon: <DollarSign className="w-4 h-4" strokeWidth={1.5} /> },
    { status: 'processing', label: 'Processing', icon: <Package className="w-4 h-4" strokeWidth={1.5} /> },
    { status: 'shipped', label: 'Shipped', icon: <Truck className="w-4 h-4" strokeWidth={1.5} /> },
    { status: 'delivered', label: 'Delivered', icon: <CheckCircle className="w-4 h-4" strokeWidth={1.5} /> }
  ];

  const currentStepIndex = statusSteps.findIndex(s => s.status === order.status);

  const handleMarkPaymentReceived = async () => {
    setError('');
    setIsUpdating(true);

    try {
      await updatePaymentStatus(order.id, 'received');
      setSuccess('Payment marked as received');
      setTimeout(() => {
        onUpdate();
        onClose();
      }, 1500);
    } catch (err: any) {
      setError(err.message || 'Failed to update payment status');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleUpdateStatus = async (newStatus: OrderStatus) => {
    setError('');
    setIsUpdating(true);

    try {
      const tracking = newStatus === 'shipped' ? trackingNumber : undefined;
      await updateOrderStatus(order.id, newStatus, tracking);

      // Send status update email
      try {
        await sendOrderStatusUpdate({
          to_email: '', // Would need to fetch retailer email
          to_name: order.retailerName,
          order_number: order.orderNumber,
          new_status: newStatus,
          tracking_number: tracking
        });
      } catch (emailErr) {
        console.error('Failed to send status email:', emailErr);
      }

      setSuccess(`Order status updated to ${newStatus.replace('_', ' ')}`);
      setTimeout(() => {
        onUpdate();
        onClose();
      }, 1500);
    } catch (err: any) {
      setError(err.message || 'Failed to update order status');
    } finally {
      setIsUpdating(false);
    }
  };

  const getNextStatus = (): OrderStatus | null => {
    if (order.status === 'pending' && order.paymentStatus !== 'received') return null;
    if (order.status === 'delivered') return null;
    const currentIndex = statusSteps.findIndex(s => s.status === order.status);
    if (currentIndex >= 0 && currentIndex < statusSteps.length - 1) {
      return statusSteps[currentIndex + 1].status;
    }
    return null;
  };

  const nextStatus = getNextStatus();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-forge-deep/70 backdrop-blur-sm" onClick={onClose}></div>

      <div className="relative bg-paper-bright shadow-float w-full max-w-3xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-forge text-paper px-6 py-5 relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center text-paper/70 hover:text-paper hover:bg-paper/10 transition-colors duration-150"
            aria-label="Close"
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
          <p className="font-mono text-[11px] tracking-micro uppercase text-ember-soft mb-2">— Order</p>
          <h2 className="font-editorial italic font-light text-2xl text-paper-warm tracking-tight">
            {order.orderNumber}.
          </h2>
          <div className="flex items-center gap-2 mt-1">
            <p className="text-paper/70 text-sm">{order.retailerName}</p>
            <span className={`inline-flex items-center px-2 py-0.5 font-mono text-[9px] tracking-micro uppercase border ${
              order.orderType === 'retail'
                ? 'bg-ember/20 text-ember-soft border-ember/40'
                : 'bg-paper/10 text-paper/80 border-paper/20'
            }`}>
              {order.orderType === 'retail' ? 'Retail' : 'Wholesale'}
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 lg:p-8 overflow-y-auto max-h-[60vh]">
          {error && (
            <div className="mb-5 bg-ember-soft/25 border-l-2 border-ember p-3 flex items-start gap-2.5">
              <AlertCircle className="w-4 h-4 text-ember flex-shrink-0 mt-0.5" strokeWidth={1.5} />
              <p className="text-ember-deep text-sm">{error}</p>
            </div>
          )}

          {success && (
            <div className="mb-5 bg-olive/15 border-l-2 border-olive p-3 flex items-start gap-2.5">
              <CheckCircle className="w-4 h-4 text-olive flex-shrink-0 mt-0.5" strokeWidth={1.5} />
              <p className="text-olive text-sm">{success}</p>
            </div>
          )}

          {/* Status Progress */}
          <div className="mb-8">
            <p className="font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-4">— Order progress</p>
            <div className="flex items-center justify-between">
              {statusSteps.map((step, index) => {
                const isCompleted = index <= currentStepIndex;
                const isCurrent = index === currentStepIndex;
                return (
                  <div key={step.status} className="flex flex-col items-center flex-1">
                    <div className={`relative flex items-center justify-center w-10 h-10 rounded-full border ${
                      isCompleted
                        ? 'bg-ember border-ember text-paper-warm'
                        : 'bg-paper border-ink-200 text-ink-500'
                    } ${isCurrent ? 'ring-4 ring-ember/15' : ''}`}>
                      {step.icon}
                    </div>
                    <span className={`text-[11px] mt-2 text-center ${isCompleted ? 'text-ink-950 font-medium' : 'text-ink-500'}`}>
                      {step.label}
                    </span>
                    {index < statusSteps.length - 1 && (
                      <div className={`absolute left-1/2 h-px top-5 ${
                        index < currentStepIndex ? 'bg-ember' : 'bg-ink-200'
                      }`} style={{ transform: 'translateX(50%)', zIndex: -1, width: 'calc(100% - 40px)' }} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            {/* Order Info */}
            <div className="space-y-4">
              <h3 className="font-editorial italic text-lg text-ink-950 tracking-tight flex items-center gap-2">
                <Package className="w-4 h-4 text-ember" strokeWidth={1.5} />
                Order details
              </h3>
              <div className="bg-paper border border-ink-200 p-4 space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-ink-500">Order date</span>
                  <span className="text-ink-900 font-medium">{order.createdAt.toLocaleString()}</span>
                </div>
                {order.customerEmail && (
                  <div className="flex justify-between gap-3">
                    <span className="text-ink-500">Email</span>
                    <span className="text-ink-900 font-medium break-all text-right">{order.customerEmail}</span>
                  </div>
                )}
                {order.customerPhone && (
                  <div className="flex justify-between">
                    <span className="text-ink-500">Phone</span>
                    <span className="text-ink-900 font-medium">{order.customerPhone}</span>
                  </div>
                )}
                {order.orderType === 'retail' && (
                  <div className="flex justify-between">
                    <span className="text-ink-500">Age verified (18+)</span>
                    <span className={`font-medium ${order.ageVerified ? 'text-olive' : 'text-brass'}`}>
                      {order.ageVerified ? 'Confirmed · ID on delivery' : 'Not confirmed'}
                    </span>
                  </div>
                )}
                {order.dateOfBirth && (
                  <div className="flex justify-between">
                    <span className="text-ink-500">Date of birth</span>
                    <span className="text-ink-900 font-medium">{order.dateOfBirth}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-ink-500">Payment status</span>
                  <span className={`font-medium ${order.paymentStatus === 'received' ? 'text-olive' : 'text-brass'}`}>
                    {order.paymentStatus === 'received' ? 'Received' : 'Awaiting transfer'}
                  </span>
                </div>
                {order.trackingNumber && (
                  <div className="flex justify-between">
                    <span className="text-ink-500">Tracking #</span>
                    <span className="font-mono text-ink-900 font-medium">{order.trackingNumber}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Shipping Address */}
            <div className="space-y-4">
              <h3 className="font-editorial italic text-lg text-ink-950 tracking-tight flex items-center gap-2">
                <MapPin className="w-4 h-4 text-ember" strokeWidth={1.5} />
                Shipping address
              </h3>
              <div className="bg-paper border border-ink-200 p-4 text-sm text-ink-900">
                <p className="font-medium text-ink-950">{order.retailerName}</p>
                <p>{order.shippingAddress.street}</p>
                <p>{order.shippingAddress.city}, {order.shippingAddress.province} {order.shippingAddress.postalCode}</p>
                <p>{order.shippingAddress.country}</p>
              </div>
            </div>

            {/* Order Items */}
            <div className="col-span-2 space-y-4">
              <h3 className="font-editorial italic text-lg text-ink-950 tracking-tight">Items</h3>
              <div className="border border-ink-200 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-paper">
                    <tr>
                      <th className="px-4 py-2.5 text-left font-mono text-[10px] tracking-micro uppercase text-ink-500">Product</th>
                      <th className="px-4 py-2.5 text-left font-mono text-[10px] tracking-micro uppercase text-ink-500">SKU</th>
                      <th className="px-4 py-2.5 text-center font-mono text-[10px] tracking-micro uppercase text-ink-500">Qty</th>
                      <th className="px-4 py-2.5 text-right font-mono text-[10px] tracking-micro uppercase text-ink-500">Price</th>
                      <th className="px-4 py-2.5 text-right font-mono text-[10px] tracking-micro uppercase text-ink-500">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ink-200">
                    {order.items.map((item, idx) => (
                      <tr key={idx} className="bg-paper-bright">
                        <td className="px-4 py-2.5 font-medium text-ink-900">{item.name}</td>
                        <td className="px-4 py-2.5 font-mono text-ink-500">{item.sku}</td>
                        <td className="px-4 py-2.5 text-center text-ink-900">{item.quantity}</td>
                        <td className="px-4 py-2.5 text-right text-ink-900">${item.price.toFixed(2)}</td>
                        <td className="px-4 py-2.5 text-right font-medium text-ink-950">${(item.price * item.quantity).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Totals */}
              <div className="bg-paper border border-ink-200 p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-ink-500">Subtotal</span>
                  <span className="text-ink-900">${order.subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-ink-500">Shipping</span>
                  <span className="text-ink-900">${order.shipping.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-ink-500">Tax</span>
                  <span className="text-ink-900">${order.tax.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-editorial italic text-lg border-t border-ink-200 pt-2 mt-2">
                  <span className="text-ink-950">Total</span>
                  <span className="text-ink-950">${order.total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Tracking Number Input (for shipping) */}
          {showTrackingInput && (
            <div className="mt-6 p-4 border border-ember/30 bg-ember-soft/15">
              <label className="block font-mono text-[10px] tracking-micro uppercase text-ember-deep mb-2">
                Tracking number
              </label>
              <input
                type="text"
                value={trackingNumber}
                onChange={(e) => setTrackingNumber(e.target.value)}
                className="w-full px-4 py-2.5 bg-paper-bright border border-ink-200 text-sm text-ink-900 placeholder:text-ink-500 focus:border-ember focus:outline-none focus:ring-1 focus:ring-ember/20 transition-colors duration-150"
                placeholder="Enter tracking number"
              />
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="px-6 lg:px-8 py-5 border-t border-ink-200 bg-paper flex gap-3 justify-end">
          {/* Payment Action */}
          {order.paymentStatus === 'awaiting_transfer' && (
            <button
              onClick={handleMarkPaymentReceived}
              disabled={isUpdating}
              className="px-5 py-2.5 bg-olive hover:bg-olive/90 text-paper-warm text-sm font-medium transition-colors duration-150 ease-editorial flex items-center gap-2 disabled:opacity-50"
            >
              {isUpdating ? (
                <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
              ) : (
                <>
                  <DollarSign className="w-4 h-4" strokeWidth={1.5} />
                  Mark payment received
                </>
              )}
            </button>
          )}

          {/* Status Progression */}
          {nextStatus && order.paymentStatus === 'received' && (
            <>
              {nextStatus === 'shipped' && !showTrackingInput ? (
                <button
                  onClick={() => setShowTrackingInput(true)}
                  className="px-5 py-2.5 bg-ember hover:bg-ember-deep text-paper-warm text-sm font-medium transition-colors duration-150 ease-editorial flex items-center gap-2"
                >
                  <Truck className="w-4 h-4" strokeWidth={1.5} />
                  Mark as shipped
                </button>
              ) : (
                <button
                  onClick={() => handleUpdateStatus(nextStatus)}
                  disabled={isUpdating || (nextStatus === 'shipped' && !trackingNumber)}
                  className="px-5 py-2.5 bg-ember hover:bg-ember-deep text-paper-warm text-sm font-medium transition-colors duration-150 ease-editorial flex items-center gap-2 disabled:opacity-50"
                >
                  {isUpdating ? (
                    <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                  ) : (
                    <>
                      {nextStatus === 'processing' && <Package className="w-4 h-4" strokeWidth={1.5} />}
                      {nextStatus === 'shipped' && <Truck className="w-4 h-4" strokeWidth={1.5} />}
                      {nextStatus === 'delivered' && <CheckCircle className="w-4 h-4" strokeWidth={1.5} />}
                      Mark as {nextStatus.replace('_', ' ')}
                    </>
                  )}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderDetailModal;
